import { uniq } from 'lodash';
import { HomePrimaryGroup, WelfareAdminDetail, WelfareAdminPermissions } from '~/types';

// Mirrors the backend `empty_permissions()` shape. Every field of the
// permissions object is required on create/update, so the form and slice
// build their payloads on top of this to guarantee a complete object.
export const EMPTY_WELFARE_PERMISSIONS: WelfareAdminPermissions = {
    primary_group_contacts: {
        can_edit: false,
        primary_group_ids: [],
    },
    category_editor: false,
    sub_category_editor: false,
    provider_editor: false,
    question_editor: false,
    cost_of_living_editor: false,
    emergency_contact_editor: false,
    feedback_editor: false,
    welfare_cases: {
        primary_group_ids: [],
    },
    welfare_case_deletion: false,
};

// The simple boolean editor permissions, in display order, used to render the
// permission checkboxes and to summarise a record in the admin list.
export const WELFARE_EDITOR_FIELDS: Array<{
    key: keyof WelfareAdminPermissions;
    label: string;
}> = [
    { key: 'category_editor', label: 'Category Editor' },
    { key: 'sub_category_editor', label: 'Sub Category Editor' },
    { key: 'provider_editor', label: 'Provider Editor' },
    { key: 'question_editor', label: 'Question Editor' },
    { key: 'cost_of_living_editor', label: 'Cost of Living Editor' },
    { key: 'emergency_contact_editor', label: 'Emergency Contact Editor' },
    { key: 'feedback_editor', label: 'Feedback Editor' },
];

// Ids from `selectedIds` that are not one of the admin's home Primary Groups.
// Used to warn when PG-scoped access is granted outside the admin's own group.
export const getOutsideHomeGroupIds = (
    selectedIds: string[],
    homeGroups: HomePrimaryGroup[] = []
): string[] => {
    const homeIds = new Set(homeGroups.map(group => group.id));

    return selectedIds.filter(pgId => !homeIds.has(pgId));
};

export const getHomeGroupNames = (homeGroups: HomePrimaryGroup[] = []): string[] =>
    homeGroups.map(group => group.name || 'Unknown group');

// What an admin can actually do within one of their home Primary Groups.
// Welfare case access is implicit for home PGs on the backend, but Primary
// Group Contacts editing is not: it needs the editor flag AND the PG selected.
export type HomeGroupAccess = {
    id: string;
    name: string;
    canEditContacts: boolean;
};

export const getHomeGroupAccess = (
    homeGroups: HomePrimaryGroup[] = [],
    pgcCanEdit: boolean,
    pgcGroupIds: string[] = []
): HomeGroupAccess[] =>
    homeGroups.map(group => ({
        id: group.id,
        name: group.name || 'Unknown group',
        canEditContacts: pgcCanEdit && pgcGroupIds.includes(group.id),
    }));

// Home PGs first (in home order), then everything else in its stored order.
export const sortHomeGroupsFirst = (ids: string[], homeGroupIds: string[]): string[] =>
    uniq([
        ...homeGroupIds.filter(pgId => ids.includes(pgId)),
        ...ids.filter(pgId => !homeGroupIds.includes(pgId)),
    ]);

export type WelfareAdminGroupScopes = {
    homeGroups: HomePrimaryGroup[];
    homeGroupAccess: HomeGroupAccess[];
    pgcIds: string[];
    casesIds: string[];
    outsidePgcIds: string[];
    outsideCasesIds: string[];
};

type WelfareAdminGroupScopeInput = {
    homeGroups?: HomePrimaryGroup[];
    pgcCanEdit: boolean;
    pgcGroupIds: string[];
    casesGroupIds: string[];
};

// Everything the form and the read-only permissions view need about an
// admin's Primary Group scopes: home groups first, home case access always
// included, and the ids that fall outside the home groups (only when the
// backend supplied them, and for contacts only while the editor is enabled).
export const getWelfareAdminGroupScopes = ({
    homeGroups = [],
    pgcCanEdit,
    pgcGroupIds,
    casesGroupIds,
}: WelfareAdminGroupScopeInput): WelfareAdminGroupScopes => {
    const homeGroupIds = homeGroups.map(group => group.id);
    const pgcIds = sortHomeGroupsFirst(pgcGroupIds, homeGroupIds);
    const casesIds = sortHomeGroupsFirst([...homeGroupIds, ...casesGroupIds], homeGroupIds);

    return {
        homeGroups,
        homeGroupAccess: getHomeGroupAccess(homeGroups, pgcCanEdit, pgcGroupIds),
        pgcIds,
        casesIds,
        outsidePgcIds:
            homeGroups.length && pgcCanEdit ? getOutsideHomeGroupIds(pgcIds, homeGroups) : [],
        outsideCasesIds: homeGroups.length ? getOutsideHomeGroupIds(casesIds, homeGroups) : [],
    };
};

// Same, derived from an admin record returned by the API.
export const getWelfareAdminGroupScopesFromRecord = (
    admin: WelfareAdminDetail
): WelfareAdminGroupScopes => {
    const pgc = admin.permissions?.primary_group_contacts;

    return getWelfareAdminGroupScopes({
        homeGroups: admin.home_primary_groups,
        pgcCanEdit: !!pgc?.can_edit,
        pgcGroupIds: pgc?.primary_group_ids || [],
        casesGroupIds:
            admin.effective_welfare_case_primary_group_ids ??
            admin.permissions?.welfare_cases?.primary_group_ids ??
            [],
    });
};
